import type { GaVueComponent } from "@/common/vueUtils";
import type { TranslationFn } from "@utils/vue-migration/translationUtils";
import { createStore, type Store } from "@/common/storeUtils";
import { type Future, GaHandlePending, GaHandleRejected, GaSuspense, initializeFuture } from "@/common/dataHandling/suspense";
import { getQuickWin } from "@newgenerated/shared/stubs/getQuickWin";
import { saveQuickWinCompleted } from "@newgenerated/shared/stubs/saveQuickWinCompleted";
import { saveQuickWinStarted } from "@newgenerated/shared/stubs/saveQuickWinStarted";
import type { ContentMetadata_QUICKWIN, ContentMetadata_SUMMARY, Language, QuickWinContent, QuickWinContentData, QuickWinContentData_INSIGHT } from "@newgenerated/shared/schema";
import { type DeepReadonly, defineComponent, type PropType } from "vue";
import { GaAlert } from "@/components/general/GaAlert";
import { GaMarkdownContent, useSafeMarkdownRenderer } from "@/components/general/GaMarkdownContent";
import { GaFormFieldInputRadioGroup } from "@/components/form/GaFormFieldInputRadioGroup";
import { AudioPlayer, getInitialPlayerState, type PlayerState } from "@/components/audio-player/AudioPlayer";
import { GaWizard } from "@/components/general/GaWizard";
import { useCustomModal } from "@/components/general/GaModal";
import "./QuickWin.scss";
import { GaFeedback } from "@/components/general/GaFeedback";
import { trackQuickWinFeedback } from "@newgenerated/shared/stubs/trackQuickWinFeedback";
import { GaButton } from "@/components/general/GaButton";
import { SharerBody } from "@/components/share/Sharer";
import type { ContentCardContext } from "@/components/cards/ContentCard";
import { assertIsDefined } from "@utils/assertion";
import { LanguageHelper } from "@utils/type/type";
import { getLocalizedSummaryReaderUrlWithNameAndDataId, getLocalizedSummaryUrlWithNameAndDataId } from "@generated-routes/designRoutes";
import type { AspContext, CustomerContext } from "@utils/widgetContextUtils";
import getCurrentLanguage = LanguageHelper.getCurrentLanguage;

export type QuickWinState = {
  currentScreen: QuickWinScreen;
  selectedAnswerIndex: number | null;
  playerState: PlayerState;
  sharing: boolean;
};

type QuickWinScreen = "MCQ" | "SCRIPT" | "TAKEAWAY" | "FINISH";

export const QuickWinPage = defineComponent({
  props: {
    quickWinId: {
      type: Number,
      required: true,
    },
    selectedAnswer: {
      type: Number,
      required: false,
    },
    onFinish: {
      type: Function as PropType<() => void>,
      required: false,
    },
    onIxStatusChanged: {
      type: Function as PropType<() => void>,
      required: false,
    },
    onCompleted: {
      type: Function as PropType<() => void>,
      required: false,
    },
    iframe: {
      type: Boolean,
      required: false,
      default: false,
    },
    ctx: {
      type: Object as PropType<ContentCardContext>,
      required: true,
    },
  },
  setup(props) {
    const store = createStore<QuickWinState>({
      currentScreen: "MCQ",
      selectedAnswerIndex: props.selectedAnswer ?? null,
      playerState: getInitialPlayerState(),
      sharing: false,
    });

    const fetchStore = createStore<Future<QuickWinContent>>(initializeFuture());

    return () => (
      <GaSuspense
        store={fetchStore}
        fetch={() => getQuickWin({ quickWinId: BigInt(props.quickWinId) })}
        render={(future) => (
          <GaHandleRejected
            unsafeFuture={future}
            renderSafe={(safeFuture) => (
              <GaHandlePending
                future={safeFuture}
                renderPending={pendingSkeleton}
                renderResult={(result) => {
                  return <QuickWinContentScreens store={store} quickWinContent={result.value} ctx={props.ctx} onFinish={props.onFinish} onIxStatusChanged={props.onIxStatusChanged} onCompleted={props.onCompleted} iframe={props.iframe} />;
                }}
              />
            )}
            ctx={{ t: props.ctx.getTranslationFunction(getCurrentLanguage()) }}
          />
        )}
      />
    );
  },
});

function pendingSkeleton(): GaVueComponent {
  return (
    <div class="quickwin-page">
      <div class="quickwin-page__header">
        <div class="quickwin-page__header-cover placeholder"></div>
        <div class="quickwin-page__header-text">
          <h3 class="placeholder">&nbsp;</h3>
          <p class="placeholder mb-0">&nbsp;</p>
        </div>
      </div>
      <div class="py-5 d-block w-100 placeholder">&nbsp;</div>
      <div class="d-flex flex-column my-2">
        <p class="placeholder my-1">&nbsp;</p>
        <p class="placeholder my-1">&nbsp;</p>
        <p class="placeholder my-1">&nbsp;</p>
        <p class="placeholder my-1">&nbsp;</p>
      </div>
      <div class="mt-5 d-flex justify-content-between">
        <div class="placeholder" style="width: 5rem; height: 2rem;"></div>
        <div class="placeholder" style="width: 5rem; height: 2rem;"></div>
      </div>
    </div>
  );
}

function QuickWinContentScreens(props: {
  store: Store<QuickWinState>;
  quickWinContent: DeepReadonly<QuickWinContent>;
  ctx: ContentCardContext;
  onFinish?: () => void;
  onIxStatusChanged?: () => void;
  onCompleted?: () => void;
  iframe: boolean;
}): GaVueComponent {
  const { store } = props;
  const t = props.ctx.getTranslationFunction(props.quickWinContent.metadata.common.language);

  const selectedAnswerIndex = store.get().selectedAnswerIndex;
  const summaryUrl = getContextBasedSummaryUrl(props.quickWinContent.summary.language, props.quickWinContent.summary.canonicalName, props.quickWinContent.summary.dataId, props.ctx.customer, props.ctx.asp);

  const actions = {
    selectAnswer: (answerIndex: number) => {
      store.sub("selectedAnswerIndex").set(answerIndex);
      // Track started
      void saveQuickWinStarted({ quickWinId: props.quickWinContent.metadata.contentId.id, body: { selectedAnswerIndex: answerIndex } }).then(() => {
        props.onIxStatusChanged?.();
      });
    },
    startReflection: () => {
      store.sub("currentScreen").set("SCRIPT");
    },
    prev: () => {
      const currentScreen = store.get().currentScreen;
      if (currentScreen === "SCRIPT") {
        store.sub("currentScreen").set("MCQ");
      } else if (currentScreen === "TAKEAWAY") {
        store.sub("currentScreen").set("SCRIPT");
      } else if (currentScreen === "FINISH") {
        store.sub("currentScreen").set("TAKEAWAY");
      }
    },
    next: () => {
      const currentScreen = store.get().currentScreen;
      if (currentScreen === "SCRIPT") {
        store.sub("currentScreen").set("TAKEAWAY");
        // Track completion
        void saveQuickWinCompleted({ quickWinId: props.quickWinContent.metadata.contentId.id }).then(() => {
          props.onIxStatusChanged?.();
          props.onCompleted?.();
        });
      } else if (currentScreen === "TAKEAWAY") {
        store.sub("currentScreen").set("FINISH");
      }
    },
    finish: props.onFinish,
  };

  const screen = (() => {
    switch (store.get().currentScreen) {
      case "MCQ":
        return (
          <QuickWinMcqScreen
            metadata={props.quickWinContent.metadata}
            content={props.quickWinContent.content}
            summary={props.quickWinContent.summary}
            selectedAnswerIndex={selectedAnswerIndex}
            onSelectAnswer={actions.selectAnswer}
            onStart={actions.startReflection}
            t={t}
          />
        );
      case "SCRIPT":
        return (
          <QuickWinScriptScreen
            metadata={props.quickWinContent.metadata}
            summary={props.quickWinContent.summary}
            content={props.quickWinContent.content}
            playerStore={store.sub("playerState")}
            onNext={actions.next}
            onBack={actions.prev}
            t={t}
          />
        );
      case "TAKEAWAY":
        return (
          <QuickWinTakeawayScreen
            metadata={props.quickWinContent.metadata}
            content={props.quickWinContent.content}
            summary={props.quickWinContent.summary}
            selectedAnswerIndex={selectedAnswerIndex}
            onNext={actions.next}
            onBack={actions.prev}
            t={t}
          />
        );
      case "FINISH":
        return (
          <QuickWinFinishScreen metadata={props.quickWinContent.metadata} summary={props.quickWinContent.summary} onFinish={actions.finish} onBack={actions.prev} ctx={props.ctx} iframe={props.iframe} shareStore={store.sub("sharing")} />
        );
    }
  })();

  return (
    <div class="quickwin-page">
      <div class="quickwin-page__header">
        <a class="quickwin-page__header-cover" href={summaryUrl} target="_blank" rel="noopener noreferrer">
          <object class="quickwin-page__header-cover-img" data={props.quickWinContent.summary.coverUri + "?s=S"} type="image/jpeg">
            <img class="quickwin-page__header-cover-img" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4AWNYuenIfwAHRgMfaGsqcAAAAABJRU5ErkJggg==" alt="" />
          </object>
        </a>
        <div class="quickwin-page__header-text">
          <h3 class="mb-1">{props.quickWinContent.metadata.common.title}</h3>
          <p class="mb-0" vHtml={t("quickwin:basedOnBy", [summaryUrl, props.quickWinContent.summary.title ?? "", props.quickWinContent.summary.authorInfo ?? ""])} />
        </div>
      </div>
      {screen}
    </div>
  );
}

function QuickWinMcqScreen(props: {
  metadata: DeepReadonly<ContentMetadata_QUICKWIN>;
  content: DeepReadonly<QuickWinContentData>;
  summary: DeepReadonly<ContentMetadata_SUMMARY>;
  selectedAnswerIndex: number | null;
  onSelectAnswer: (answerIndex: number) => void;
  onStart: () => void;
  t: TranslationFn;
}): GaVueComponent {
  const { t } = props;

  return (
    <GaWizard
      currentStep={1}
      maxStep={4}
      content={
        <>
          <div class="py-2">
            <GaAlert variant={"light-blue"}>
              <h4>
                <i class="ico-question-circle" /> {props.metadata.quickWinType === "INSIGHT" ? t("quickwin:beforeWeBegin") : t("quickwin:pauseToConsider")}
              </h4>
              <p>{renderMarkdownToHtml(props.content.mcqQuestion)}</p>
            </GaAlert>
          </div>

          <div class="py-2 ps-2">
            <GaFormFieldInputRadioGroup
              name="mcq-answer"
              value={props.selectedAnswerIndex != null ? String(props.selectedAnswerIndex) : ""}
              options={props.content.mcqOptions.map((option: string, index: number) => ({
                label: option,
                value: String(index),
              }))}
              onUpdateValue={(value: string) => props.onSelectAnswer(Number(value))}
            />
          </div>

          {props.selectedAnswerIndex != null && (
            <div class="py-2">
              <GaAlert variant={"light"}>{renderMarkdownToHtml(props.content.mcqFeedback)}</GaAlert>
            </div>
          )}
        </>
      }
      nextButton={
        <GaButton onClick={props.onStart} disabled={props.selectedAnswerIndex == null}>
          {t("quickwin:action.start", [props.metadata.quickWinType === "INSIGHT" ? t("quickwin:insight") : t("quickwin:reflection")])}
        </GaButton>
      }
    />
  );
}

function QuickWinScriptScreen(props: {
  metadata: DeepReadonly<ContentMetadata_QUICKWIN>;
  content: DeepReadonly<QuickWinContentData>;
  summary: DeepReadonly<ContentMetadata_SUMMARY>;
  playerStore: Store<PlayerState>;
  onNext: () => void;
  onBack: () => void;
  t: TranslationFn;
}): GaVueComponent {
  const { t: t } = props;

  return (
    <GaWizard
      currentStep={2}
      maxStep={4}
      previousButton={
        <GaButton variant={"primary-alt"} onClick={props.onBack}>
          {t("quickwin:action.back")}
        </GaButton>
      }
      nextButton={<GaButton onClick={props.onNext}>{t("quickwin:action.next")}</GaButton>}
      content={
        <>
          <h5>{t("quickwin:listenOrRead")}</h5>
          <div class="py-2">
            <AudioPlayer
              audioPlayerProps={{
                store: props.playerStore,
                contentItem: { popoutLink: undefined, cover: undefined },
                audioUrl: `/quick-win-audio/${props.metadata.contentId.id}.mp3`,
                disabled: false,
                t: t,
              }}
            />
          </div>
          <div class="py-2 mb-2">
            <GaAlert variant="light">
              <i class="ico-info-circle" />
              {t("quickwin:audioplayer.info")}
            </GaAlert>
          </div>
          <div class="script-content py-2 px-2">
            <p>
              <b>{t("quickwin:transcript.title")}</b>
            </p>
            <p>{renderMarkdownToHtml(props.content.script)}</p>
          </div>
        </>
      }
    />
  );
}

function QuickWinTakeawayScreen(props: {
  metadata: DeepReadonly<ContentMetadata_QUICKWIN>;
  summary: DeepReadonly<ContentMetadata_SUMMARY>;
  content: DeepReadonly<QuickWinContentData>;
  selectedAnswerIndex: number | null;
  onNext: () => void;
  onBack: () => void;
  t: TranslationFn;
}): GaVueComponent {
  const { t: t } = props;

  const isInsight = props.content.quickWinType === "INSIGHT";
  const userAnswer = isInsight && props.selectedAnswerIndex != null ? props.content.mcqOptions[props.selectedAnswerIndex] : null;
  const correctAnswer = isInsight ? (props.content as QuickWinContentData_INSIGHT).mcqCorrectAnswer : null;

  return (
    <GaWizard
      currentStep={3}
      maxStep={4}
      content={
        <>
          {isInsight && (
            <div>
              <div class="py-2">
                <GaAlert variant="light">
                  <h4>
                    <i class="ico-question-circle" />
                    {t("quickwin:takeaway.question")}
                  </h4>
                  <p>{renderMarkdownToHtml(props.content.mcqQuestion)}</p>
                </GaAlert>
              </div>
              {userAnswer !== correctAnswer && userAnswer != null && (
                <div class="py-2">
                  <GaAlert variant="warning">
                    <h4>
                      <i class="ico-speech-bubble" />
                      {t("quickwin:takeaway.yourAnswer")}
                    </h4>
                    <p>{renderMarkdownToHtml(userAnswer)}</p>
                  </GaAlert>
                </div>
              )}
              <div class="py-2">
                <GaAlert variant="success">
                  <h4>
                    <i class="ico-check-circle" />
                    {userAnswer === correctAnswer ? t("quickwin:takeaway.yourAnswerCorrect") : t("quickwin:takeaway.answer")}
                  </h4>
                  <p>{renderMarkdownToHtml(correctAnswer ?? "")}</p>
                </GaAlert>
              </div>
            </div>
          )}
          <div class="py-2">
            <GaAlert variant="light-blue">
              <h4>
                <i class="ico-star" />
                {t("quickwin:takeaway")}
              </h4>
              <p>{renderMarkdownToHtml(props.content.takeaway)}</p>
            </GaAlert>
          </div>
        </>
      }
      previousButton={
        <GaButton variant={"primary-alt"} onClick={props.onBack}>
          {t("quickwin:action.back")}
        </GaButton>
      }
      nextButton={<GaButton onClick={props.onNext}>{t("quickwin:action.finish")}</GaButton>}
    />
  );
}

function QuickWinFinishScreen(props: {
  metadata: DeepReadonly<ContentMetadata_QUICKWIN>;
  summary: DeepReadonly<ContentMetadata_SUMMARY>;
  onFinish?: () => void;
  onBack: () => void;
  ctx: ContentCardContext;
  iframe: boolean;
  shareStore: Store<boolean>;
}): GaVueComponent {
  const t = props.ctx.getTranslationFunction(props.metadata.common.language);

  const onFeedback = async (isPositive: boolean): Promise<void> => {
    const result = await trackQuickWinFeedback({ quickWinId: props.metadata.contentId.id, body: { isPositive: isPositive } });
    if (result.kind === "error") {
      throw Error(`QuickWin feedback submission failed, error: ${result.errorCode}, message: ${result.errorMessage}`);
    }
  };
  const link = props.metadata.common.link;
  assertIsDefined(link);

  const shareUrl = new URL("/skill-boosts/spotlight?skillBoostId=" + props.metadata.contentId.id.toString(), window.location.origin);

  const isSharing = props.shareStore.get();

  return (
    <GaWizard
      currentStep={4}
      maxStep={4}
      content={
        <>
          <div class="text-center py-3">
            {isSharing ? (
              <SharerBody shareMedia={["COPY_LINK"]} contentItemType={"QUICKWIN"} contentItemId={props.metadata.contentId.id} link={shareUrl} currentLanguage={props.metadata.common.language} t={t} />
            ) : (
              <>
                <i class="ico-certificate quickwin-progress__finish-icon fs-1 mb-3" />
                <h2 class="fw-bold mb-3"> {t("quickwin:finish.title")}</h2>
                <p class="mb-3 quickwin-page__finishMessage" vHtml={t("quickwin:finish.message")} />
                <div class="quickwin-page__feedback">
                  <GaFeedback
                    title={t("quickwin:feedback.title", "How valuable did you find this Quick Win?")}
                    changeHandler={(isPositive: boolean) => onFeedback(isPositive)}
                    mailTitle={t("quickwin:feedback.mail.title", [props.metadata.common.title, props.summary.title ?? ""])}
                    mailBody={t("quickwin:feedback.mail.body")}
                    showMailFeedbackLink={true}
                    buttonBorder={true}
                    positiveFeedbackText={t("general:yes")}
                    negativeFeedbackText={t("general:no")}
                  />
                </div>
              </>
            )}
          </div>
        </>
      }
      previousButton={
        props.onFinish != null ? (
          isSharing ? (
            <GaButton variant={"primary-alt"} onClick={() => props.shareStore.set(false)}>
              {t("quickwin:action.back")}
            </GaButton>
          ) : (
            <GaButton variant={"primary-alt"} onClick={() => props.shareStore.set(true)}>
              <i class="ico-share-alt2 pe-1" />
              {t("general:share.short")}
            </GaButton>
          )
        ) : undefined
      }
      nextButton={
        <GaButton variant={"primary"} href={getContextBasedSummaryUrl(props.summary.language, props.summary.canonicalName, props.summary.dataId, props.ctx.customer, props.ctx.asp)}>
          {t("quickwin:action.readSummary")}
        </GaButton>
      }
    />
  );
}

function getContextBasedSummaryUrl(language: Language, canonicalName: string, dataId: number, customer: CustomerContext, asp: AspContext | null): string {
  return asp == null && customer !== null ? getLocalizedSummaryReaderUrlWithNameAndDataId(language, canonicalName, dataId.toString()) : getLocalizedSummaryUrlWithNameAndDataId(language, canonicalName, dataId.toString());
}

function renderMarkdownToHtml(input: string): GaVueComponent {
  return (
    <GaMarkdownContent
      input={input}
      defaultRenderer={useSafeMarkdownRenderer(() => (
        <></>
      ))}
    />
  );
}

export const showQuickWinPageAsModal = async (id: number, ctx: ContentCardContext, onIxStatusChanged?: () => void, onCompleted?: () => void, selectedAnswer?: number | null, onClose?: () => void): Promise<void> => {
  await useCustomModal()({
    body: ({ close }) => <QuickWinPage quickWinId={Number(id)} ctx={ctx} onFinish={close} onIxStatusChanged={onIxStatusChanged} onCompleted={onCompleted} selectedAnswer={selectedAnswer ?? undefined} />,
    size: "lg",
  });

  onClose?.();
};
